<div class="dash-shell">
  @if (isLoadingContexts()) {
    <div class="dash-skeleton" style="min-height: 80px"></div>
    <div class="contexts-grid">
      @for (i of [1, 2, 3]; track i) {
        <div class="dash-skeleton" style="min-height: 180px"></div>
      }
    </div>
  } @else if (contextsError()) {
    <div class="dash-panel">
      <div class="dash-empty">
        <span class="dash-empty__icon"><app-icon name="alert-triangle" [size]="32" /></span>
        <p class="dash-empty__title">Couldn't load your dashboard</p>
        <p class="dash-empty__body">Something went wrong on our side.</p>
        <button mat-stroked-button (click)="reload()">Try again</button>
      </div>
    </div>
  } @else if (contexts()?.length === 0) {
    <!-- Empty state: no contexts yet -->
    <mat-card appearance="outlined" class="onboarding-card">
      <mat-card-content>
        <div class="onboarding-card__intro">
          <span class="onboarding-card__badge">
            <app-icon name="sparkles" [size]="20" />
          </span>
          <h2 class="onboarding-card__heading">Welcome to CapX Flow</h2>
          <p class="onboarding-card__body">
            You don't have any businesses yet. Getting funded takes just three steps — here's how it
            works.
          </p>
        </div>

        <ol class="onboarding-steps" aria-label="How to get funded in three steps">
          @for (step of onboardingSteps; track step.number) {
            <li class="onboarding-step">
              <span class="onboarding-step__icon" aria-hidden="true">
                <app-icon [name]="step.icon" [size]="24" />
              </span>
              <div class="onboarding-step__text">
                <span class="onboarding-step__eyebrow">Step {{ step.number }}</span>
                <h3 class="onboarding-step__title">{{ step.title }}</h3>
                <p class="onboarding-step__description">{{ step.description }}</p>
              </div>
            </li>
          }
        </ol>

        <a
          mat-flat-button
          routerLink="/entity/create"
          class="onboarding-card__cta"
          data-testid="register-business-cta"
        >
          <span>Register your business</span>
          <app-icon name="arrow-right" [size]="18" />
        </a>
      </mat-card-content>
    </mat-card>
  } @else {
    <!-- Hero greeting -->
    <header class="dash-hero">
      <div class="dash-hero__body">
        <p class="dash-hero__eyebrow">{{ greeting() }}</p>
        <h1 class="dash-hero__title">
          @if (firstName(); as name) {
            {{ name }}
          } @else {
            Welcome back
          }
        </h1>
        @if (showStats()) {
          <p class="dash-hero__sub">
            You have {{ stats().businesses }} business{{ stats().businesses === 1 ? '' : 'es' }}.
          </p>
        }
      </div>
      <div class="dash-hero__actions">
        <a mat-stroked-button routerLink="/entity/create" class="add-business-btn">
          <app-icon name="plus" [size]="16" />
          <span>Add a business</span>
        </a>
      </div>
    </header>

    <!-- Stat strip -->
    @if (showStats()) {
      <section class="landing-stats" aria-label="Your funding at a glance">
        <div class="landing-stat">
          <span class="landing-stat__label">Businesses</span>
          <span class="landing-stat__value">{{ stats().businesses }}</span>
        </div>
        <div class="landing-stat">
          <span class="landing-stat__label">Active funding</span>
          <span class="landing-stat__value">{{ stats().active }}</span>
        </div>
        <div class="landing-stat" [class.landing-stat--alert]="stats().pending > 0">
          <span class="landing-stat__label">Needs your attention</span>
          <span class="landing-stat__value">{{ stats().pending }}</span>
        </div>
      </section>
    }

    <!-- Context cards grid -->
    <section aria-label="Your contexts">
      <div class="contexts-grid">
        @for (ctx of sortedContexts(); track ctx.uuid ?? ctx.role; let i = $index) {
          <mat-card
            appearance="outlined"
            class="context-card dash-enter"
            [class.context-card--attention]="ctx.funding?.needs_attention"
            [style.--dash-index]="i"
            role="button"
            tabindex="0"
            [attr.aria-label]="'Open ' + ctx.label"
            (click)="navigateTo(ctx.url)"
            (keydown.enter)="navigateTo(ctx.url)"
            (keydown.space)="navigateTo(ctx.url); $event.preventDefault()"
          >
            <mat-card-content>
              @if (ctx.funding?.needs_attention) {
                <span class="context-card__flag" aria-label="Needs your attention">
                  <app-icon name="alert-triangle" [size]="13" />
                </span>
              }

              <div class="context-card__header">
                <span class="context-card__avatar" aria-hidden="true">
                  <app-icon [name]="getContextIcon(ctx.type)" [size]="22" />
                </span>
                <div class="context-card__heading">
                  <p class="context-card__type" [matTooltip]="getContextTypeTooltip(ctx.type)">
                    {{ getContextTypeLabel(ctx.type) }}
                  </p>
                  <h3 class="context-card__label">{{ ctx.label }}</h3>
                </div>
              </div>

              @if (ctx.type === 'entity' && ctx.funding; as funding) {
                <p class="context-card__funding" [class]="fundingToneClass(funding.tone)">
                  <span class="context-card__funding-dot" aria-hidden="true"></span>
                  <span>{{ funding.product }} — {{ funding.label }}</span>
                </p>
              }

              @if (ctx.type === 'entity') {
                <ul class="context-card__details" aria-label="Business details">
                  @if (ctx.entity_type_display) {
                    <li class="context-card__detail">
                      <app-icon name="tag" [size]="15" />
                      <span>{{ ctx.entity_type_display }}</span>
                    </li>
                  }
                  @if (ctx.industry) {
                    <li class="context-card__detail">
                      <app-icon name="briefcase" [size]="15" />
                      <span>{{ ctx.industry }}</span>
                    </li>
                  }
                  @if (getTradingSince(ctx.trading_since); as trading) {
                    <li class="context-card__detail">
                      <app-icon name="calendar" [size]="15" />
                      <span>{{ trading }}</span>
                    </li>
                  }
                </ul>
              }

              <div class="context-card__footer">
                <span class="context-card__role" [matTooltip]="getRoleTooltip(ctx.role)">{{
                  getRoleLabel(ctx.role)
                }}</span>
                @if (ctx.type === 'entity' && ctx.status) {
                  <app-status-chip [status]="ctx.status" />
                }
                <app-icon class="context-card__chevron" name="arrow-right" [size]="18" />
              </div>
            </mat-card-content>
          </mat-card>
        }
      </div>
    </section>
  }

  @if (hasDeletedBusinesses()) {
    <footer class="dashboard-footer">
      <a mat-stroked-button routerLink="/entity/deleted" class="deleted-business-link">
        <app-icon name="trash" [size]="16" />
        <span>Deleted businesses ({{ deletedCount() }})</span>
      </a>
    </footer>
  }
</div>
